import React from 'react';
import { Link } from 'react-router-dom';

import WhiskeyLogo from './layout/WhiskeyLogo';
import LucideIcon from './LucideIcon';
import { TOPICS } from '../config/topics';

const BASIC_TECHNIQUES = [
  'Zero-Shot Prompting',
  'Clear, Direct Instructions',
  'Role & System Prompting',
  'Structured Output & Data Separation',
  'Few-Shot Prompting',
  'Chain-of-Thought Prompting',
  'Self-Consistency',
  'Prompt Chaining & Task Decomposition',
];

const APPLIED_TOPICS = TOPICS.filter((t) => t.id !== 'basic-prompt-engineering');

const STEPS = [
  { n: '01', title: 'Read the concept', body: 'A short, focused lesson introduces one real prompt-engineering technique -- no filler video.' },
  { n: '02', title: 'Write your prompt', body: 'Apply it to a real scenario in the same editor you’ll use for every challenge -- not a multiple-choice quiz.' },
  { n: '03', title: 'Get instant feedback', body: 'Claude grades your prompt against a rubric and tells you exactly what to change, right away.' },
];

const STATS = [
  { icon: 'topic-agent-design', label: '101 interactive lessons' },
  { icon: 'topic-coding-debugging', label: '10 agent frameworks' },
  { icon: 'Zap', label: 'Rubric-driven AI evaluations' },
];

// Tier 2/3 of the real curriculum plan (docs/build-plan/07-curriculum-content-plan.md,
// 10-curriculum-expansion.md) -- these tracks are live (frontend/src/config/tracksCatalog.ts),
// not roadmap. Keep this list in sync with tracksCatalog.ts's FRAMEWORK_TRACKS/PRODUCTION_TRACK
const FRAMEWORK_TRACKS = [
  { name: 'LangGraph', blurb: 'Build stateful multi-agent workflows with StateGraph, explicit routing, and human-in-the-loop.' },
  { name: 'CrewAI', blurb: 'Orchestrate collaborative agent crews with defined roles, tools, and delegation.' },
  { name: 'OpenAI Agents SDK', blurb: 'Master @function_tool, model handoffs, Pydantic structured output, and guardrails.' },
  { name: 'Google ADK', blurb: 'Build with sub_agents delegation, AgentTool invocation, session context, and tool callbacks.' },
  { name: 'AWS Strands & AgentCore', blurb: 'Develop model-driven agent loops, MCP tool integration, and runtime cloud deployments.' },
  { name: 'LlamaIndex', blurb: 'Construct FunctionAgents, agentic RAG query engines, and multi-agent workflows.' },
  { name: 'LangChain', blurb: 'Agent architecture fundamentals -- create_agent, tools, and message memory.' },
  { name: 'Production & Observability', blurb: 'OpenTelemetry tracing, prompt caching, model routing, and cost optimization.' },
];

const BYO_MODEL_STEPS = [
  { n: '01', title: 'Platform key', status: 'In development', live: false, body: "Planned built-in Claude access for practice, with no API key to configure." },
  { n: '02', title: 'Your API key', status: 'Coming soon', live: false, body: 'Bring your own Anthropic, OpenAI, or Bedrock key and practice on your own account.' },
  { n: '03', title: 'Self-hosted', status: 'Coming soon', live: false, body: 'Point Whiskey Sensei at a self-hosted or open-weight model of your choice.' },
];

const PRICING_TIERS = [
  { name: 'Free', price: '$0', tag: 'Coming soon', live: false, desc: 'Full access to foundational prompting, study rungs, and 5 daily AI evaluations.', cta: true },
  { name: 'Pro', price: '$19 / mo', tag: 'Coming soon', live: false, desc: 'Unlimited AI grading, full agent framework tracks, sandbox execution, and Ask Whiskey tutor.', cta: true },
  { name: 'Enterprise', price: 'Coming soon', tag: 'Teams', live: false, desc: 'Team workspaces, custom track paths, SSO, and engineer skill analytics.', cta: false },
];

const Landing: React.FC = () => {
  return (
    <div className="ws-shell" style={{ background: 'var(--paper)', minHeight: '100vh' }}>
      {/* Nav */}
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '0 28px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
            <WhiskeyLogo size={22} />
            <span className="ws-display" style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>Whiskey Sensei</span>
          </Link>
          <div className="ws-landing-nav-links">
            <a href="#how-it-works" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary)', textDecoration: 'none' }}>How it works</a>
            <a href="#frameworks" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary)', textDecoration: 'none' }}>Agent Frameworks</a>
            <a href="#practice" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary)', textDecoration: 'none' }}>Foundations</a>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary)' }}>Coming soon</span>
            <a href="/early-access.html" className="btn-live" style={{ background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: 13.5, padding: '9px 18px', borderRadius: 8, textDecoration: 'none' }}>
              Contact us
            </a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '72px 28px 88px' }}>
        <div className="ws-landing-hero">
          <div className="ws-fade-up" style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ember-strong)' }}>
              Coming soon · In development
            </span>
            <h1 className="ws-display" style={{ margin: 0, fontSize: 42, fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.15, color: 'var(--text-primary)' }}>
              Master AI Agent &amp; LLM Systems Engineering through interactive code.
            </h1>
            <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: 'var(--text-secondary)', maxWidth: 500 }}>
              Build and evaluate stateful agents with LangGraph, CrewAI, OpenAI Agents SDK, Google ADK, and AWS Strands. Real 3-rung practice, automated rubrics, and spaced retrieval.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <a href="/early-access.html" className="btn-live" style={{ background: 'var(--ember)', color: 'white', fontWeight: 700, fontSize: 15, padding: '13px 26px', borderRadius: 10, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                Request early access
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M7 4l6 6-6 6" /></svg>
              </a>
              <a href="#how-it-works" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', textDecoration: 'none' }}>
                See how it works
              </a>
            </div>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)' }}>Public signup and login are not available yet. Questions or feedback? Request early access, or <a href="https://tony-yang-architect.netlify.app/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>visit my personal website</a>.</p>
            <div className="ws-landing-stats" style={{ marginTop: 8 }}>
              {STATS.map((s) => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: 'var(--ember-strong)' }}><LucideIcon name={s.icon} size={17} /></span>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Proof panel -- shows the real product: an agent architecture challenge + rubric score */}
          <div className="ws-fade-up" style={{ animationDelay: '80ms', background: 'var(--ink)', borderRadius: 14, overflow: 'hidden', boxShadow: '0 20px 50px oklch(0.2 0.02 260 / 0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 16px', borderBottom: '1px solid oklch(1 0 0 / 0.08)' }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'oklch(0.65 0.15 25)' }} />
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'oklch(0.75 0.13 85)' }} />
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'oklch(0.65 0.15 155)' }} />
              <span className="ws-mono" style={{ marginLeft: 8, fontSize: 11.5, color: 'var(--text-on-dark-dim)' }}>langgraph_agent_flow.py</span>
            </div>
            <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-on-dark-dim)' }}>Challenge Objective</span>
                <p className="ws-mono" style={{ margin: '6px 0 0', fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-on-dark)' }}>
                  StateGraph vs create_agent: Write a deterministic 4-node moderation pipeline with TypedDict schema and condition-based review routing.
                </p>
              </div>
              <div style={{ borderLeft: '2px solid var(--success)', paddingLeft: 12 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--success)' }}>Rubric Score: 100 / 100</span>
                <p style={{ margin: '4px 0 0', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-on-dark-dim)' }}>Deterministic entry step, explicit threshold branch, and join node correctly defined.</p>
              </div>
              <div style={{ borderLeft: '2px solid var(--gold)', paddingLeft: 12 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--gold)' }}>Technique Verified</span>
                <p style={{ margin: '4px 0 0', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-on-dark-dim)' }}>TypedDict state schema and node registration adhere to LangGraph v0.2+ conventions.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div id="how-it-works" style={{ background: 'var(--surface-sunken)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '72px 28px' }}>
          <h2 className="ws-display" style={{ margin: '0 0 40px', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center' }}>
            How a lesson actually works
          </h2>
          <div className="ws-landing-steps">
            {STEPS.map((step) => (
              <div key={step.n} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span className="ws-display" style={{ fontSize: 13, fontWeight: 700, color: 'var(--ember-strong)' }}>{step.n}</span>
                <h3 className="ws-display" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{step.title}</h3>
                <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Practice topics */}
      <div id="practice" style={{ maxWidth: 1120, margin: '0 auto', padding: '80px 28px' }}>
        <h2 className="ws-display" style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center' }}>
          Practice that maps to real work
        </h2>
        <p style={{ margin: '0 0 44px', fontSize: 14.5, color: 'var(--text-tertiary)', textAlign: 'center' }}>
          Start with the fundamentals, then apply them to the kinds of tasks people actually use AI for.
        </p>

        <div style={{ marginBottom: 40, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 28 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ember-strong)' }}>Foundations</span>
          <h3 className="ws-display" style={{ margin: '6px 0 18px', fontSize: 19, fontWeight: 700, color: 'var(--text-primary)' }}>Basic Prompt Engineering</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px 24px' }}>
            {BASIC_TECHNIQUES.map((t) => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text-secondary)' }}>
                <LucideIcon name="CheckCircle2" size={15} />
                {t}
              </div>
            ))}
          </div>
        </div>

        <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--volt-strong)' }}>Applied practice</span>
        <div className="ws-landing-topics-grid" style={{ marginTop: 14 }}>
          {APPLIED_TOPICS.map((topic) => (
            <div key={topic.id} className="card-live" style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
              <span style={{ color: 'var(--volt-strong)' }}><LucideIcon name={topic.icon} size={18} /></span>
              <span className="ws-display" style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{topic.name}</span>
              <span style={{ fontSize: 12, lineHeight: 1.45, color: 'var(--text-tertiary)' }}>{topic.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Agentic Frameworks: Live Now */}
      <div id="frameworks" style={{ background: 'var(--volt-soft)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="ws-fade-up" style={{ maxWidth: 1120, margin: '0 auto', padding: '72px 28px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', marginBottom: 40 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'white', background: 'var(--success)', borderRadius: 999, padding: '5px 14px' }}>
              In development
            </span>
            <h2 className="ws-display" style={{ margin: 0, fontSize: 32, fontWeight: 700, color: 'var(--text-primary)' }}>
              10 Modern Agent Frameworks &amp; Production Systems
            </h2>
            <p style={{ margin: 0, fontSize: 15, color: 'var(--text-secondary)', maxWidth: 580 }}>
              Go beyond simple text prompts. Build, orchestrate, and evaluate real autonomous systems using the industry's leading production agent libraries.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
            {FRAMEWORK_TRACKS.map((track) => (
              <div key={track.name} className="card-live" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span className="ws-display" style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{track.name}</span>
                <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary)' }}>{track.blurb}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <a href="/early-access.html" className="btn-live" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: 14, padding: '11px 22px', borderRadius: 8, textDecoration: 'none' }}>
              Request early access
            </a>
          </div>
        </div>
      </div>

      {/* Coming soon: Bring your own model */}
      <div style={{ background: 'var(--ink)' }}>
        <div className="ws-fade-up" style={{ maxWidth: 1120, margin: '0 auto', padding: '72px 28px' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink)', background: 'var(--gold)', borderRadius: 999, padding: '5px 14px' }}>
              Coming soon
            </span>
            <h2 className="ws-display" style={{ margin: '14px 0 8px', fontSize: 26, fontWeight: 700, color: 'var(--text-on-dark)' }}>
              Bring your own model
            </h2>
            <p style={{ margin: '0 auto', fontSize: 14.5, color: 'var(--text-on-dark-dim)', maxWidth: 480 }}>
              Practice today on the platform's built-in Claude access. Soon, plug in your own key or self-hosted model -- same lessons, your infrastructure.
            </p>
          </div>
          <div className="ws-landing-steps">
            {BYO_MODEL_STEPS.map((step) => (
              <div key={step.n} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="ws-display" style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-on-dark-dim)' }}>{step.n}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: step.live ? 'var(--success)' : 'var(--gold)' }}>{step.status}</span>
                </div>
                <h3 className="ws-display" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-on-dark)' }}>{step.title}</h3>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--text-on-dark-dim)' }}>{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '80px 28px' }}>
        <h2 className="ws-display" style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center' }}>
          Planned offerings
        </h2>
        <p style={{ margin: '0 0 44px', fontSize: 14.5, color: 'var(--text-tertiary)', textAlign: 'center' }}>
          Whiskey Sensei is still in development. Public signup and login are not open yet. Plans and pricing below are provisional and may change before launch.
        </p>
        <div className="ws-landing-topics-grid">
          {PRICING_TIERS.map((tier) => (
            <div key={tier.name} style={{
              display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--surface)', borderRadius: 14, padding: 24,
              border: tier.live ? '2px solid var(--ink)' : '1px solid var(--border)',
            }}>
              <span style={{ alignSelf: 'flex-start', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: tier.live ? 'var(--success)' : 'var(--text-tertiary)', background: tier.live ? 'oklch(0.65 0.15 155 / 0.12)' : 'var(--surface-sunken)', borderRadius: 999, padding: '4px 10px' }}>
                {tier.tag}
              </span>
              <span className="ws-display" style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{tier.name}</span>
              <span className="ws-display" style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>{tier.price}</span>
              <p style={{ margin: '0 0 6px', fontSize: 13, lineHeight: 1.5, color: 'var(--text-tertiary)', flex: 1 }}>{tier.desc}</p>
              {tier.cta && (
                <a href="/early-access.html" className="btn-live" style={{ textAlign: 'center', background: 'var(--ember)', color: 'white', fontWeight: 700, fontSize: 13.5, padding: '10px', borderRadius: 8, textDecoration: 'none' }}>
                  Contact us
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <WhiskeyLogo size={16} />
            <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>Whiskey Sensei</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
            <a href="/early-access.html" style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Request early access</a>
            {/* A plain address for anyone whose question is not an early-access
                request: that form is gated behind Turnstile and rejects personal
                email domains, so it is the wrong door for a general enquiry. */}
            <a href="mailto:support@whiskeysensei.com" style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>support@whiskeysensei.com</a>
            <a href="https://tony-yang-architect.netlify.app/" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>About Tony · Personal website</a>
            <Link to="/privacy" style={{ fontSize: 12, color: 'var(--text-tertiary)', textDecoration: 'none' }}>Privacy</Link>
            <Link to="/terms" style={{ fontSize: 12, color: 'var(--text-tertiary)', textDecoration: 'none' }}>Terms</Link>
            <Link to="/data-rights" style={{ fontSize: 12, color: 'var(--text-tertiary)', textDecoration: 'none' }}>Your data</Link>
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>&copy; {new Date().getFullYear()} Whiskey Sensei</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
